import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { getUserHighestRole, hasRole } from '../../utils/roleHelpers';
import supabase from "../../supabaseClient";
import { useNavigate } from 'react-router-dom';
import { useHeaderActions } from '../../contexts/HeaderActionsContext.jsx';
import CourseCarousel from './Academylms/CourseCarousel/CourseCarousel';
import './AcademyLandingPage.css';
import { FaDoorOpen, FaArrowLeft } from 'react-icons/fa';

// Security constants
const ADMIN_ACCESS_CODE_HASH = '8c6976e5b5410415bde908bd4dee15dfb167a9c873fc4bb8a81f6f2ab448a918'; // hash of 'admin'
const IS_DEVELOPMENT = true; // Force development mode
const UPGRADE_COOLDOWN_MINUTES = IS_DEVELOPMENT ? 1 : 30;
const MAX_ATTEMPTS = IS_DEVELOPMENT ? 5 : 3;
const LOCKOUT_DURATION_MINUTES = IS_DEVELOPMENT ? 5 : 60;
const SESSION_DURATION = 3600000; // 1 hour in milliseconds
const RATE_LIMIT_WINDOW = 300000; // 5 minutes in milliseconds
const MAX_REQUESTS_PER_WINDOW = IS_DEVELOPMENT ? 20 : 5;

const AcademyLandingPage = () => {
  const { user, loading } = useAuth();
  const { roles, activeRole, setActiveRole, isSuperadmin, ALL_ROLES, loading: permLoading } = usePermissions();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const userRole = getUserHighestRole(roles);
  const [accessCode, setAccessCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [upgradeAttempts, setUpgradeAttempts] = useState(0);
  const [cooldownEnd, setCooldownEnd] = useState(null);
  const [securityToken, setSecurityToken] = useState('');
  const [lastUpgradeAttempt, setLastUpgradeAttempt] = useState(null);
  const [sessionToken, setSessionToken] = useState('');
  const [requestCount, setRequestCount] = useState(0);
  const [lastRequestTime, setLastRequestTime] = useState(null);
  const [showRoleDock, setShowRoleDock] = useState(false);

  // --- Role selection UI state ---
  const roleOptions = roles.map(r => ({ value: r.role, label: r.label || r.role }));
  const impersonateOptions = isSuperadmin
    ? ALL_ROLES.filter(r => !roles.some(ar => ar.role === r.key)).map(r => ({ value: r.key, label: `[Impersonate] ${r.label}` }))
    : [];
  const allOptions = [...roleOptions, ...impersonateOptions];

  const { setLeftSlot } = useHeaderActions();

  const headerLauncher = useMemo(() => (
    <button
      type="button"
      className="auth-header-launcher-button"
      onClick={() => setShowRoleDock(true)}
      aria-label="Access Academy LMS"
      title="Access Academy"
    >
      <FaDoorOpen />
      <FaArrowLeft />
    </button>
  ), []);

  useEffect(() => {
    if (!(allOptions.length > 0)) return;
    setLeftSlot(headerLauncher);
    return () => setLeftSlot(null);
  }, [allOptions.length, headerLauncher, setLeftSlot]);
  const [selectedRole, setSelectedRole] = useState(activeRole || (roleOptions[0]?.value || ''));

  // Update selectedRole when roles data loads or activeRole changes
  useEffect(() => {
    if (!selectedRole && (activeRole || roleOptions.length > 0)) {
      const newSelectedRole = activeRole || roleOptions[0]?.value || '';
      console.log('Setting selectedRole to:', newSelectedRole);
      setSelectedRole(newSelectedRole);
    }
  }, [activeRole, roleOptions, selectedRole]);

  const handleEnter = () => {
    console.log('handleEnter called with selectedRole:', selectedRole);
    console.log('Current user:', user);
    console.log('Current roles:', roles);
    console.log('Current activeRole:', activeRole);
    
    if (!user || !selectedRole) {
      console.error('No user or selected role for navigation');
      return;
    }
    
    console.log('Setting activeRole to:', selectedRole);
    setActiveRole(selectedRole);
    console.log('Navigating to role-specific LMS path:', `/app/academy/lms/${selectedRole}`);
    
    // Navigate to the role-specific LMS section
    navigate(`/app/academy/lms/${selectedRole}`);
  };

  const renderRolePanel = () => {
    if (allOptions.length > 1) {
      return (
        <div className="role-entry-panel" style={{ maxWidth: 400, margin: '0 auto', marginTop: 40 }}>
          <h2 className="role-entry-title">Welcome to VirtuLingu Academy</h2>
          <label htmlFor="landing-role-select" className="role-entry-label">Select your role</label>
          <select
            id="landing-role-select"
            className="role-select"
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}
          >
            {roleOptions.length > 0 && (
              <optgroup label="Assigned Roles">
                {roleOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </optgroup>
            )}
            {impersonateOptions.length > 0 && (
              <optgroup label="Impersonate">
                {impersonateOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </optgroup>
            )}
          </select>
          <button className="role-enter-btn" onClick={handleEnter}>
            Enter as {allOptions.find(opt => opt.value === selectedRole)?.label || (selectedRole ? selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1) : 'Role')}
          </button>
          {isSuperadmin && impersonateOptions.some(opt => opt.value === selectedRole) && (
            <span className="impersonate-badge">Impersonating</span>
          )}
        </div>
      );
    }

    if (roleOptions.length === 1) {
      return (
        <div className="role-entry-panel" style={{ maxWidth: 400, margin: '0 auto', marginTop: 40 }}>
          <h2 className="role-entry-title">Welcome to VirtuLingu Academy</h2>
          <button
            onClick={() => {
              const role = roleOptions[0]?.value;
              console.log('Single role button clicked:', role);
              setActiveRole(role);
              navigate(`/app/academy/lms/${role}`);
            }}
            className="role-enter-btn"
          >
            Enter as {roleOptions[0]?.label || 'Role'}
          </button>
        </div>
      );
    }

    return null;
  };


  // Generate a secure session token
  const generateSessionToken = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  // Validate session token
  const validateSession = async () => {
    if (!sessionToken) {
      const newToken = generateSessionToken();
      setSessionToken(newToken);
      localStorage.setItem('sessionToken', newToken);
      localStorage.setItem('sessionStart', Date.now().toString());
      return true;
    }

    const sessionStart = parseInt(localStorage.getItem('sessionStart') || '0');
    if (Date.now() - sessionStart > SESSION_DURATION) {
      // Session expired
      const newToken = generateSessionToken();
      setSessionToken(newToken);
      localStorage.setItem('sessionToken', newToken);
      localStorage.setItem('sessionStart', Date.now().toString());
      return false;
    }

    return sessionToken === localStorage.getItem('sessionToken');
  };

  // Check rate limiting
  const checkRateLimit = async () => {
    const now = Date.now();
    if (!lastRequestTime || (now - lastRequestTime) > RATE_LIMIT_WINDOW) {
      setRequestCount(1);
      setLastRequestTime(now);
      return true;
    }

    if (requestCount >= MAX_REQUESTS_PER_WINDOW) {
      return false;
    }

    setRequestCount(prev => prev + 1);
    return true;
  };

  useEffect(() => {
    // Fetch user profile data and role when user is available
    const fetchUserProfile = async () => {
      if (user) {
        if (!await validateSession()) {
          alert('Your session has expired. Please log in again.');
          navigate('/login');
          return;
        }

        setProfileLoading(true);
        try {
          // Fetch profile data with simplified query
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', user.id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
          } else if (profileData) {
            setDisplayName(profileData.display_name || user.email);
          }

          // Fetch user roles (new schema: roles array from PermissionsContext)
          // No direct query here; rely on roles from usePermissions()
          // Legacy single-role fetching removed

          if (permLoading) return; // Wait for permissions to load

          if (!roles || roles.length === 0) {
            // No roles assigned; optionally trigger a default role assignment via API or inform user
            setSecurityToken('');
            setLastUpgradeAttempt(null);
          } else {
            // Use the first assigned role for legacy logic (for security token, etc.)
            setSecurityToken(roles[0]?.security_token || '');
            setLastUpgradeAttempt(roles[0]?.last_upgrade_attempt || null);
          }

          // Check for existing lockout or cooldown
          const now = new Date();
          if (roles[0]?.last_upgrade_attempt) {
            const lastAttempt = new Date(roles[0].last_upgrade_attempt);
            const lockoutEnd = new Date(lastAttempt.getTime() + LOCKOUT_DURATION_MINUTES * 60000);
            if (lockoutEnd > now) {
              setCooldownEnd(lockoutEnd);
            }
          }

          // Fetch security logs if needed for additional verification
          if (upgrading || showVerification) {
            const { data: securityData, error: securityError } = await supabase
              .from('academy_security_logs')
              .select('*')
              .eq('user_id', user.id)
              .order('assigned_at', { ascending: false })
              .limit(1);

            if (securityError) {
              console.error('Error fetching security logs:', securityError);
            } else if (securityData && securityData.length > 0) {
              const lastAttempt = new Date(securityData[0].created_at);
              const lockoutEnd = new Date(lastAttempt.getTime() + LOCKOUT_DURATION_MINUTES * 60000);
              if (securityData[0].attempt_count >= MAX_ATTEMPTS && lockoutEnd > now) {
                setCooldownEnd(lockoutEnd);
              }
            }
          }
        } catch (error) {
          console.error('Unexpected error:', error);
        } finally {
          setProfileLoading(false);
        }
      }
    };

    fetchUserProfile();
  }, [user, navigate]);

  // Generate a secure random token
  const generateSecurityToken = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  // Function to hash the access code
  const hashAccessCode = async (code) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(code);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const checkCooldownAndLockout = async () => {
    if (!user) return false;

    const now = new Date();
    
    // Check cooldown
    if (lastUpgradeAttempt) {
      const cooldownEndTime = new Date(lastUpgradeAttempt);
      cooldownEndTime.setMinutes(cooldownEndTime.getMinutes() + UPGRADE_COOLDOWN_MINUTES);
      if (now < cooldownEndTime) {
        const minutesLeft = Math.ceil((cooldownEndTime - now) / 60000);
        setVerificationError(`Please wait ${minutesLeft} minutes before trying again.`);
        return false;
      }
    }

    // Check lockout
    if (cooldownEnd && now < cooldownEnd) {
      const minutesLeft = Math.ceil((cooldownEnd - now) / 60000);
      setVerificationError(`Account locked. Try again in ${minutesLeft} minutes.`);
      return false;
    }

    return true;
  };

  const handleVerifyAccessCode = async () => {
    console.log('Verifying access code:', accessCode); // Debug log
    
    if (!await validateSession()) {
      alert('Your session has expired. Please log in again.');
      navigate('/login');
      return;
    }

    if (!await checkRateLimit()) {
      setVerificationError(`Too many attempts. Please try again in ${Math.ceil(RATE_LIMIT_WINDOW/60000)} minutes.`);
      return;
    }

    if (!await checkCooldownAndLockout()) return;

    try {
      // Get client IP and geolocation with fallback for development
      let geoData = { ip: '127.0.0.1', country: 'development', region: 'local', city: 'localhost' };
      
      if (process.env.NODE_ENV === 'production') {
        try {
          const geoResponse = await fetch('https://ipapi.co/json/');
          if (geoResponse.ok) {
            geoData = await geoResponse.json();
          }
        } catch (error) {
          console.warn('Geolocation service unavailable, using fallback');
        }
      }

      // Log verification attempt with enhanced details
      await supabase
        .from('academy_security_logs')
        .insert([{
          user_id: user.id,
          action: 'verify_attempt',
          ip_address: geoData.ip,
          user_agent: navigator.userAgent,
          details: {
            country: geoData.country,
            region: geoData.region,
            city: geoData.city,
            session_token: sessionToken,
            browser_fingerprint: await generateBrowserFingerprint(),
            is_development: IS_DEVELOPMENT
          },
          created_at: new Date().toISOString()
        }]);

      const hashedInput = await hashAccessCode(accessCode);
      console.log('Hashed input:', hashedInput); // Debug log
      console.log('Expected hash:', ADMIN_ACCESS_CODE_HASH); // Debug log
      
      // In development, also accept 'dev_admin' as access code
      const isDevelopmentBypass = IS_DEVELOPMENT && accessCode === 'dev_admin';
      console.log('Is development bypass:', isDevelopmentBypass); // Debug log

      if ((hashedInput === ADMIN_ACCESS_CODE_HASH || isDevelopmentBypass) && securityToken) {
        console.log('Hash matched or development bypass active'); // Debug log
        
        // Verify security token
        const { data: tokenValid } = await supabase
          .from('academy_user_roles')
          .select('security_token')
          .eq('user_id', user.id)
          .order('assigned_at', { ascending: false })
          .maybeSingle();

        console.log('Token validation:', { 
          hasToken: !!tokenValid, 
          tokenMatches: tokenValid?.security_token === securityToken 
        }); // Debug log

        if (tokenValid && tokenValid.security_token === securityToken) {
          setShowVerification(false);
          setVerificationError('');
          handleUpgradeToSuperAdmin();
        } else {
          // In development, allow bypass of token verification
          if (IS_DEVELOPMENT && isDevelopmentBypass) {
            console.log('Development mode: bypassing token verification'); // Debug log
            setShowVerification(false);
            setVerificationError('');
            handleUpgradeToSuperAdmin();
          } else {
            throw new Error('Invalid security token');
          }
        }
      } else {
        console.log('Access code validation failed'); // Debug log
        const newAttempts = upgradeAttempts + 1;
        setUpgradeAttempts(newAttempts);
        
        if (newAttempts >= MAX_ATTEMPTS) {
          const lockoutEnd = new Date(Date.now() + LOCKOUT_DURATION_MINUTES * 60000);
          setCooldownEnd(lockoutEnd);
          setVerificationError(`Maximum attempts exceeded. Account locked for ${LOCKOUT_DURATION_MINUTES} minutes.`);
        } else {
          setVerificationError(`Invalid access code. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`);
        }
        setAccessCode('');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationError('An error occurred during verification.');
    }
  };

  // Generate browser fingerprint for additional security
  const generateBrowserFingerprint = async () => {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency,
      navigator.deviceMemory,
      navigator.platform
    ].join('|');

    const encoder = new TextEncoder();
    const data = encoder.encode(components);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleUpgradeToSuperAdmin = async () => {
    if (!user || !securityToken) return;
    
    if (!await validateSession()) {
      alert('Your session has expired. Please log in again.');
      navigate('/login');
      return;
    }

    setUpgrading(true);
    try {
      // Get geolocation data for enhanced logging
      let geoData = { ip: '127.0.0.1', country: 'development', region: 'local', city: 'localhost' };
      
      // Generate new security token
      const newSecurityToken = generateSecurityToken();
      const browserFingerprint = await generateBrowserFingerprint();

      // Log the upgrade attempt with enhanced details
      await supabase
        .from('academy_security_logs')
        .insert([{
          user_id: user.id,
          action: 'role_upgrade',
          ip_address: geoData.ip,
          user_agent: navigator.userAgent,
          details: {
            previous_role: getUserHighestRole(roles),
            new_role: 'superadmin',
            country: geoData.country,
            region: geoData.region,
            city: geoData.city,
            session_token: sessionToken,
            browser_fingerprint: browserFingerprint,
            security_token: securityToken
          },
          assigned_at: new Date().toISOString()
        }]);

      // First, delete any existing role
      await supabase
        .from('academy_user_roles')
        .delete()
        .eq('user_id', user.id);

      // Then insert the superadmin role with new security token (new schema)
      // Need to look up superadmin role_id
      const { data: superadminRole } = await supabase
        .from('academy_roles')
        .select('id')
        .eq('role', 'superadmin')
        .maybeSingle();
      if (!superadminRole) {
        alert('Superadmin role not found in roles table.');
        setUpgrading(false);
        return;
      }
      const { error } = await supabase
        .from('academy_user_roles')
        .insert([{ 
          user_id: user.id, 
          role_id: superadminRole.id,
          security_token: newSecurityToken,
          last_upgrade_attempt: new Date().toISOString(),
          assigned_at: new Date().toISOString()
        }]);

      if (error) {
        console.error('Error upgrading to superadmin:', error);
        alert('Failed to upgrade to superadmin. Please check console for details.');
      } else {
        alert('Successfully upgraded to superadmin!');
        // Redirect to superadmin dashboard after successful upgrade
        navigate('/app/academy/superadmin-dashboard');
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      alert('An unexpected error occurred. Please check console for details.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleDashboardAccess = () => {
    if (!user) {
      // Redirect to login or show login modal
      return;
    }

    // Navigate to the unified LMS (which will show the appropriate role-based content)
    const userRole = getUserHighestRole(roles);
    if (userRole) {
      setActiveRole(userRole);
      navigate(`/app/academy/lms/${userRole}`);
    } else {
      navigate('/app/academy/lms');
    }
  };

  const handleLMSLayoutAccess = () => {
    if (!user) return;
    // Navigate to the unified LMS (which will show the appropriate role-based content)
    const userRole = getUserHighestRole(roles);
    if (userRole) {
      setActiveRole(userRole);
      navigate(`/app/academy/lms/${userRole}`);
    } else {
      navigate('/app/academy/lms');
    }
  };

  // --- MAIN RETURN (role panel already rendered above) ---
  console.log('Academy Landing Debug:', {
    allOptionsLength: allOptions.length,
    roleOptionsLength: roleOptions.length,
    impersonateOptionsLength: impersonateOptions.length,
    selectedRole,
    activeRole,
    roles,
    isSuperadmin
  });

  return (
    <div className="academy-landing">
      {showRoleDock && (
        <div className="academy-role-dock-overlay" onClick={() => setShowRoleDock(false)}>
          <div
            className="academy-role-dock-panel"
            onClick={e => e.stopPropagation()}
          >
            <div className="academy-role-dock-header">
              <span className="academy-role-dock-title">Academy LMS Access</span>
              <button
                type="button"
                className="academy-role-dock-close"
                onClick={() => setShowRoleDock(false)}
                aria-label="Close LMS access dock"
              >
                ✕
              </button>
            </div>
            <div className="academy-role-dock-body">
              {renderRolePanel()}
            </div>
          </div>
        </div>
      )}

      <div className="academy-landing-panel page-panel">
        {/* --- Rest of landing page UI follows below --- */}
        <section className="academy-landing-main">
          <div className="academy-welcome">
            <h1>Welcome{displayName ? `, ${displayName}` : ''}!</h1>
            <p>Your personalized language learning journey starts here.</p>
          </div>
        </section>
        <section className="academy-discover">
          <h2>Discover Courses</h2>
          <CourseCarousel />
        </section>

        <section className="academy-work-with-us">
          <h2>Work with us</h2>
          <p>Interested in joining our team? <a href="#">Apply here</a>.</p>
        </section>
      </div>

      {/* Mobile FAB for role dock */}
      {allOptions.length > 0 && (
        <button
          type="button"
          className="academy-mobile-role-fab"
          onClick={() => setShowRoleDock(true)}
          aria-label="Access Academy LMS"
          title="Access Academy"
        >
          <FaDoorOpen style={{ fontSize: '1.3rem' }} />
          <FaArrowLeft style={{ fontSize: '0.9rem' }} />
        </button>
      )}
    </div>
  );
}

export default AcademyLandingPage;
